/**
 * Claim an Arthaus Email address — the onboarding step. Arrives prefilled from the suggestion
 * (the artist's handle) or from ?claim=<name> when the artist came from the arthaus.email page.
 */

import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CheckCircle2, Inbox as InboxIcon, Loader2, Sparkles, XCircle, Palette, PenLine } from 'lucide-react'
import { inboxApi } from '../api/inboxApi'
import { useClaimInbox } from '../hooks/useInbox'
import type { Availability } from '../types'

const REASON: Record<string, string> = {
  invalid: 'Use at least two letters or numbers.',
  reserved: 'That name is reserved.',
  taken: 'Someone already has that one.',
}

interface Props {
  domain: string
  configured: boolean
  suggestion: Availability | null
  initialName?: string | null
}

export function ClaimInboxCard({ domain, configured, suggestion, initialName }: Props) {
  const [name, setName] = useState(initialName || suggestion?.localPart || '')
  const [fromName, setFromName] = useState(suggestion?.fromName || '')
  const [check, setCheck] = useState<Availability | null>(initialName ? null : suggestion)
  const [checking, setChecking] = useState(false)
  const claim = useClaimInbox()

  // Debounced live availability, so the artist knows before they press the button.
  useEffect(() => {
    if (!name.trim()) {
      setCheck(null)
      return
    }
    setChecking(true)
    const handle = setTimeout(async () => {
      try {
        setCheck(await inboxApi.checkAvailability(name))
      } catch {
        setCheck(null)
      } finally {
        setChecking(false)
      }
    }, 350)
    return () => clearTimeout(handle)
  }, [name])

  const canClaim = configured && check?.available && !claim.isPending

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-primary/10 p-2">
              <InboxIcon className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">Your studio inbox</CardTitle>
              <CardDescription>
                A real email address for your art, with a studio manager that reads every message.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="inbox-name">Your address</Label>
            <div className="flex items-center rounded-md border focus-within:ring-2 focus-within:ring-ring">
              <Input
                id="inbox-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="border-0 text-lg focus-visible:ring-0"
                placeholder="yourname"
                autoComplete="off"
              />
              <span className="whitespace-nowrap pr-3 text-lg text-muted-foreground">@{domain}</span>
            </div>
            <div className="h-5 text-sm">
              {checking ? (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> Checking…
                </span>
              ) : check?.available ? (
                <span className="flex items-center gap-1 text-emerald-600">
                  <CheckCircle2 className="h-3.5 w-3.5" /> {check.address} is yours to claim
                </span>
              ) : check ? (
                <span className="flex items-center gap-1 text-destructive">
                  <XCircle className="h-3.5 w-3.5" /> {REASON[check.reason || 'invalid']}
                </span>
              ) : null}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="inbox-from">Name people see</Label>
            <Input
              id="inbox-from"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="Your artist name"
              maxLength={78}
            />
          </div>

          {!configured && (
            <p className="text-sm text-destructive">Arthaus Email is not switched on for this environment yet.</p>
          )}

          <Button
            size="lg"
            className="w-full"
            disabled={!canClaim}
            onClick={() => check?.localPart && claim.mutate({ localPart: check.localPart, fromName: fromName || undefined })}
          >
            {claim.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Claim {check?.available ? check.address : 'address'}
          </Button>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-3">
        {[
          {
            icon: <Sparkles className="h-4 w-4" />,
            title: 'Every message triaged',
            body: 'Commission leads, collectors, press and spam are sorted the moment they arrive.',
          },
          {
            icon: <Palette className="h-4 w-4" />,
            title: 'Requests become commissions',
            body: '“Do you ever do custom pieces?” is scored, briefed, and one click from a project.',
          },
          {
            icon: <PenLine className="h-4 w-4" />,
            title: 'Replies drafted, never sent for you',
            body: 'A reply in your voice is waiting on each lead. You edit and send.',
          },
        ].map((f) => (
          <Card key={f.title}>
            <CardContent className="space-y-1 pt-6">
              <div className="flex items-center gap-2 font-medium">
                {f.icon}
                {f.title}
              </div>
              <p className="text-sm text-muted-foreground">{f.body}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
